import type { Permission } from '#shared/types';
import { routes } from '#client/lib/routes';

/**
 * The palette's static action list.
 *
 * Deliberately short. The research on lab command palettes is specific that the
 * high-value payload is jump-to-record plus a handful of destinations — not an
 * exhaustive command registry. A palette listing forty commands is a menu with
 * extra steps.
 *
 * `keywords` exist so "queue" finds the worklist and "new patient" finds
 * registration, without those words having to appear in the visible label.
 */
interface Command {
  id: string;
  label: string;
  href: string;
  group: 'Go to' | 'Create';
  keywords?: string;
  capability?: Permission;
}

export const COMMANDS: Command[] = [
  {
    id: 'home',
    label: 'Home',
    href: routes.dashboard,
    group: 'Go to',
    keywords: 'dashboard overview start',
  },
  {
    id: 'worklist',
    label: 'Worklist',
    href: routes.worklist(),
    group: 'Go to',
    keywords: 'queue pending work reports',
  },
  {
    id: 'worklist-registered',
    label: 'Awaiting results',
    href: routes.worklist({ status: 'registered' }),
    group: 'Go to',
    keywords: 'registered new queue not started',
  },
  {
    id: 'worklist-progress',
    label: 'In progress',
    href: routes.worklist({ status: 'in_progress' }),
    group: 'Go to',
    keywords: 'started partial bench',
  },
  {
    id: 'worklist-completed',
    label: 'Completed reports',
    href: routes.worklist({ status: 'completed' }),
    group: 'Go to',
    keywords: 'finished done signed',
  },
  {
    id: 'worklist-overdue',
    label: 'Overdue work',
    href: routes.worklist({ overdue: true }),
    group: 'Go to',
    keywords: 'late past due breach tat',
  },
  {
    id: 'worklist-unpaid',
    label: 'Unpaid reports',
    href: routes.worklist({ unpaid: true }),
    group: 'Go to',
    keywords: 'balance owing money outstanding',
  },
  {
    id: 'patients',
    label: 'Patients',
    href: routes.patients,
    group: 'Go to',
    keywords: 'people list',
  },
  {
    id: 'lab-tests',
    label: 'Lab tests',
    href: routes.labTests,
    group: 'Go to',
    keywords: 'catalogue menu panel price',
    capability: 'labTest.view',
  },
  {
    id: 'parameters',
    label: 'Parameters',
    href: routes.testParameters,
    group: 'Go to',
    keywords: 'analyte reference range',
    capability: 'testParameter.view',
  },
  {
    id: 'comments',
    label: 'Report comments',
    href: routes.reportComments,
    group: 'Go to',
    keywords: 'footnote canned',
    capability: 'reportComment.view',
  },
  {
    id: 'remarks',
    label: 'Report remarks',
    href: routes.reportRemarks,
    group: 'Go to',
    keywords: 'note interpretation canned',
    capability: 'reportRemark.view',
  },
  {
    id: 'panels',
    label: 'Panels',
    href: routes.panels,
    group: 'Go to',
    keywords: 'clinic contract discount referral',
    capability: 'panel.view',
  },
  {
    id: 'staff',
    label: 'Staff',
    href: routes.users,
    group: 'Go to',
    keywords: 'users accounts roles',
    capability: 'staff.view',
  },

  {
    id: 'register',
    label: 'Register a patient',
    href: routes.patientNew,
    group: 'Create',
    keywords: 'new add intake booking',
    capability: 'patient.create',
  },
  {
    id: 'new-lab-test',
    label: 'Add a lab test',
    href: routes.labTestNew,
    group: 'Create',
    keywords: 'new test catalogue',
    capability: 'labTest.create',
  },
  {
    id: 'new-parameter',
    label: 'Add a parameter',
    href: routes.testParameterNew,
    group: 'Create',
    keywords: 'new analyte range',
    capability: 'testParameter.create',
  },
  {
    id: 'procedures',
    label: 'Test procedures',
    href: routes.testProcedures,
    group: 'Go to',
    keywords: 'sop stp method principle bench how',
    capability: 'procedure.view',
  },
  {
    id: 'order-requests',
    label: 'Test requests',
    href: routes.orderRequests,
    group: 'Go to',
    keywords: 'inbox fax referral doctor asked pending',
    capability: 'orderRequest.view',
  },
  {
    id: 'requisitions',
    label: 'Reagent orders',
    href: routes.requisitions,
    group: 'Go to',
    keywords: 'purchase requisition reorder supplier buy delivery',
    capability: 'requisition.view',
  },
  {
    id: 'new-order-request',
    label: 'Take a test request',
    href: routes.orderRequestNew,
    group: 'Create',
    keywords: 'fax referral doctor asked new request',
    capability: 'orderRequest.create',
  },
  {
    id: 'day-close',
    label: 'Day close',
    href: routes.dayClose,
    group: 'Go to',
    keywords: 'drawer cash end of day takings close shift',
    capability: 'dayClose.view',
  },
  {
    id: 'calculator',
    label: 'Charge calculator',
    href: routes.calculator,
    group: 'Go to',
    keywords: 'price quote discount how much cost',
    capability: 'pricing.quote',
  },
  {
    id: 'profit-loss',
    label: 'Profit and loss',
    href: routes.profitLoss,
    group: 'Go to',
    keywords: 'pnl margin costs expenses surplus',
    capability: 'finance.profitLoss',
  },
  {
    id: 'income',
    label: 'Income',
    href: routes.income,
    group: 'Go to',
    keywords: 'revenue earnings billed collected breakdown',
    capability: 'finance.income',
  },
  {
    id: 'new-expense',
    label: 'Record an expense',
    href: routes.expenseNew,
    group: 'Create',
    keywords: 'cost salary rent bill spend paid out',
    capability: 'expense.create',
  },
  {
    id: 'new-requisition',
    label: 'Raise a reagent order',
    href: routes.requisitionNew,
    group: 'Create',
    keywords: 'purchase requisition reorder buy stock',
    capability: 'requisition.create',
  },
  {
    id: 'new-import',
    label: 'Import a spreadsheet',
    href: routes.importNew,
    group: 'Create',
    keywords: 'excel xlsx upload rate list history migrate',
    capability: 'import.run',
  },
  {
    id: 'new-staff',
    label: 'Add a staff account',
    href: routes.userNew,
    group: 'Create',
    keywords: 'new user invite',
    capability: 'staff.create',
  },
];

/** Simple substring match over the label and its keywords. */
export function matchCommand(command: Command, query: string): boolean {
  if (!query) return true;
  const needle = query.toLowerCase();
  return `${command.label} ${command.keywords ?? ''}`.toLowerCase().includes(needle);
}
